/**
 * @fileoverview Dollar Liquidity Observatory Types
 * Defines observatory-specific types for the Dollar Liquidity view model.
 * @module observatories/dollar_liquidity/types
 */

/**
 * @typedef {'up' | 'down' | 'flat'} TrendDirection
 */

/**
 * @typedef {'low' | 'medium' | 'high' | 'extreme'} Severity
 */

/**
 * View model status indicating data availability.
 * @typedef {"ready"|"partial"|"data-unavailable"|"error"} ViewModelStatus
 */

/**
 * @typedef {Object} HeadlineMetric
 * @property {string} id - Unique metric identifier
 * @property {string} label - Display label
 * @property {number|string} value - Current value
 * @property {string} [unit] - Optional unit (e.g., '%', 'bps')
 * @property {number} [change] - Optional change value
 * @property {TrendDirection} [direction] - Optional trend direction
 * @property {Severity} [severity] - Optional severity level
 */

/**
 * @typedef {Object} TimeseriesPoint
 * @property {string} t - ISO timestamp
 * @property {number} v - Value
 */

/**
 * @typedef {Object} PanelPayload
 * @property {TimeseriesPoint[]} [series] - Timeseries data for chart panels
 * @property {string} [text] - Text content for narrative panels
 * @property {Object} [metadata] - Additional metadata
 */

/**
 * @typedef {Object} Panel
 * @property {string} id - Unique panel identifier
 * @property {'timeseries' | 'text' | 'metric'} type - Panel type
 * @property {string} title - Panel title
 * @property {string} [description] - Optional panel description
 * @property {PanelPayload} payload - Panel data payload
 */

/**
 * @typedef {Object} DataSourceInfo
 * @property {"mock"|"fred"} mode - Current data source mode
 * @property {Object} [errors] - Per-series error flags
 */

/**
 * @typedef {Object} DollarLiquidityViewModel
 * @property {'dollar_liquidity'} observatoryId - Fixed observatory identifier
 * @property {string} timeRange - Current time range
 * @property {HeadlineMetric[]} headlineMetrics - Key metrics for dashboard header
 * @property {Panel[]} panels - Dashboard panels
 * @property {string} [narrative] - Optional narrative summary
 * @property {ViewModelStatus} [status] - Data availability status
 * @property {string} [error] - Error message if status is "error"
 * @property {DataSourceInfo} [dataSource] - Data source information
 */

/**
 * @typedef {Object} DollarLiquidityInputContext
 * @property {string} timeRange - Time range for data retrieval
 * @property {function(string): Promise<*>} getSignal - Signal retrieval function
 */

/**
 * Factory function to create an empty DollarLiquidityViewModel.
 * @param {string} timeRange - Time range for the view model
 * @returns {DollarLiquidityViewModel}
 */
export function createEmptyViewModel(timeRange) {
  return {
    observatoryId: "dollar_liquidity",
    timeRange,
    headlineMetrics: [],
    panels: [],
    narrative: undefined,
    status: "ready",
    error: undefined,
    dataSource: undefined,
  };
}

/**
 * Type guard placeholder for runtime validation.
 * @param {*} obj - Object to validate
 * @returns {boolean} True if valid DollarLiquidityViewModel
 */
export function isDollarLiquidityViewModel(obj) {
  return (
    obj !== null &&
    typeof obj === "object" &&
    obj.observatoryId === "dollar_liquidity" &&
    typeof obj.timeRange === "string" &&
    Array.isArray(obj.headlineMetrics) &&
    Array.isArray(obj.panels)
  );
}
